import {
  buildUnitInterestFormula,
  deriveCalculationMethod,
} from 'components/lazy-unit-interest-card/lazy-unit-interest-card';
import type {
  FormulaDetailsResponse,
  FormulaDetailsTractFormula,
} from 'types/api-responses';
import { format8 } from 'utils/number';

import styles from './formula-breakdown.module.scss';

/**
 * Formats a formula operand: up to 8 decimals with trailing zeros trimmed
 * (e.g. 0.33370000 → "0.3337", 640 → "640"). Em-dash for null. Used for the
 * acreage / NRA inputs; the highlighted *results* use fixed 8-decimal `format8`.
 *
 * @param {number | null | undefined} value - The operand.
 * @return {string} The trimmed display string.
 */
const operand = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return Number(value.toFixed(8)).toString();
};

/**
 * A stacked numerator-over-denominator fraction (matches the mock).
 *
 * @param {object} props - Component props.
 * @param {string} props.num - Numerator text.
 * @param {string} props.den - Denominator text.
 * @return {JSX.Element} The rendered fraction.
 */
const Fraction = ({ num, den }: { num: string; den: string }): JSX.Element => (
  <span className={styles.fraction}>
    <span className={styles.numerator}>{num}</span>
    <span className={styles.denominator}>{den}</span>
  </span>
);

/**
 * The Tract NRI / Tract WI fraction. Mirrors the canonical `TractNRICard`
 * branches (NRA: `(NRA / 8) / gross acres`; NMA: `NMA / gross acres × royalty`,
 * with the OGL / ORRI variants), rendered as a stacked fraction. WI is the
 * full-interest form (no royalty divisor).
 *
 * @param {object} props - Component props.
 * @param {FormulaDetailsTractFormula} props.tract - The tract formula fields.
 * @param {boolean} props.isWI - Render the WI form (no royalty) when true.
 * @return {JSX.Element} The fraction (+ optional multiplier).
 */
const TractFraction = ({
  tract,
  isWI,
}: {
  tract: FormulaDetailsTractFormula;
  isWI: boolean;
}): JSX.Element => {
  const gross = operand(tract.grossAcres);
  // Reuse the shared derivation (NRA vs NMA) so this fraction can't drift from
  // the unit-interest card's branch.
  const isNma = deriveCalculationMethod(tract) === 'nma';
  const instrument = (tract.instrumentType ?? '').toUpperCase();

  if (isWI) {
    // Working interest: net acres over gross acres, no royalty divisor.
    return (
      <Fraction
        num={`${operand(tract.nra ?? tract.nma)} ac`}
        den={`${gross} ac`}
      />
    );
  }

  if (!isNma) {
    // NRA: (NRA / 8) / gross acres — the "/ 8" is the canonical 1/8 royalty
    // factor baked into TractNRICard's NRA branch.
    return <Fraction num={`${operand(tract.nra)} / 8`} den={gross} />;
  }

  // NMA variants carry the royalty as a trailing multiplier.
  const fraction = <Fraction num={operand(tract.nma)} den={gross} />;
  if (instrument === 'ORRI') return fraction;
  return (
    <>
      {fraction}
      <span className={styles.operator}>×</span>
      {instrument === 'OGL' ? (
        <span>
          (1 − {operand(tract.leaseRoyaltyRate)} − {operand(tract.orriBurdens)})
        </span>
      ) : (
        <span>{operand(tract.leaseRoyaltyRate)}</span>
      )}
    </>
  );
};

interface FormulaBreakdownProps {
  /** The canonical AM formula shape (slot-detail `formulas.current` /
   *  `formulas.atConfirmation`) — identical to `/nri_calculations/:id/formula_details`. */
  formula: FormulaDetailsResponse;
  /** The resulting NRI or WI value (the highlighted UAT result). */
  value: number | null;
  calculationType: 'NRI' | 'WI';
}

/**
 * Renders one column of the per-subtract formula breakdown — Tract NRI/WI and
 * UAT NRI/WI as the stacked-fraction expressions in the mock, drawn directly
 * from the BE formula payload (no recomputation; results come straight from the
 * payload). The UAT operands reuse the canonical `buildUnitInterestFormula`
 * mapping so this stays in lock-step with `UnitInterestCard`'s math.
 *
 * @param {FormulaBreakdownProps} props - Component props.
 * @return {JSX.Element} The formula column.
 */
export const FormulaBreakdown = ({
  formula,
  value,
  calculationType,
}: FormulaBreakdownProps): JSX.Element => {
  const isWI = calculationType === 'WI';
  const tract = isWI ? formula.tractWiFormula : formula.tractNriFormula;
  const tractValue = isWI
    ? (formula.nriFormula?.tractWi ?? null)
    : (formula.nriFormula?.tractNri ?? null);

  const { allocationMethod, acreageFormula, lateralFormula } =
    buildUnitInterestFormula(formula.nriFormula);
  const unit = formula.nriFormula?.measureUnit ?? 'ac';

  // UAT = Tract × (UAT measure / unit measure) × (participation / UAT GIS).
  // Operand sources mirror `buildUnitNRICalculation` exactly (acreage vs lateral).
  let firstNum = '—';
  let firstDen = '—';
  let secondNum = '—';
  let secondDen = '—';
  if (allocationMethod === 'lateral_length' && lateralFormula) {
    firstNum = `${operand(lateralFormula.uatFt)} ft`;
    firstDen = operand(lateralFormula.totalLlFt);
    secondNum = operand(parseFloat(lateralFormula.participationTractAc));
    secondDen = `${operand(lateralFormula.uatGisAc)} ac`;
  } else if (acreageFormula) {
    firstNum = `${operand(acreageFormula.uatAc)} ${unit}`;
    firstDen = operand(acreageFormula.unitTractGrossAcresAc);
    secondNum = operand(parseFloat(acreageFormula.participationTractAc));
    secondDen = `${operand(acreageFormula.uatGisAc)} ${unit}`;
  }

  return (
    <div className={styles.breakdown}>
      <div className={styles.label}>Tract {calculationType}</div>
      <div className={styles.expression}>
        {tract !== null && <TractFraction tract={tract} isWI={isWI} />}
        <span className={styles.operator}>=</span>
        <span className={styles.result}>{format8(tractValue, '—')}</span>
      </div>

      <div className={styles.label}>UAT {calculationType}</div>
      <div className={styles.expression}>
        <span>{format8(tractValue, '—')}</span>
        <span className={styles.operator}>×</span>
        <Fraction num={firstNum} den={firstDen} />
        <span className={styles.operator}>×</span>
        <Fraction num={secondNum} den={secondDen} />
        <span className={styles.operator}>=</span>
        <span className={styles.result}>{format8(value, '—')}</span>
      </div>
    </div>
  );
};
